import * as THREE from 'three';
import { seaSurface, type PlanetDef } from '@hyperlight/shared';

const HALF = 768, SEGMENTS = 128;

/** Minimal opaque renderer for explicit static basin profiles. It deliberately
 * adds no wave displacement, foam, reflection pass or hidden support offset.
 * Floating floes and source-connected lava flows are separate geometry work. */
export class BasinSurface {
  readonly far: THREE.Mesh;
  readonly near: THREE.Mesh;
  private readonly up = new THREE.Vector3();
  private readonly east = new THREE.Vector3();
  private readonly north = new THREE.Vector3();
  private readonly point = new THREE.Vector3();
  private readonly anchor = new THREE.Vector3();
  private readonly time = { value: 0 };
  readonly stats = { rebuilds: 0, vertices: (SEGMENTS + 1) ** 2 };

  constructor(readonly def: PlanetDef) {
    const kind = seaSurface(def);
    if (kind === 'water' || def.seaRadius === null) throw new Error('BasinSurface requires an explicit non-water sea');
    const ice = kind === 'grounded-ice';
    const material = (origin: THREE.Vector3) => {
      const m = new THREE.MeshStandardMaterial({
        color: ice ? new THREE.Color(.68, .74, .78) : new THREE.Color(.045, .034, .032),
        roughness: ice ? .76 : .94, metalness: 0,
      });
      m.onBeforeCompile = shader => {
        shader.uniforms.uBasinOrigin = { value: origin };
        shader.uniforms.uBasinTime = this.time;
        shader.vertexShader = 'uniform vec3 uBasinOrigin; varying vec3 vBasinLocal;\n' + shader.vertexShader;
        shader.vertexShader = shader.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\nvBasinLocal = position + uBasinOrigin;');
        shader.fragmentShader = `varying vec3 vBasinLocal; uniform float uBasinTime;
          float basinHash(vec2 p) { return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453); }
          float basinFacet(vec2 p) {
            vec2 c=floor(p),f=fract(p);
            float a=basinHash(c),b=basinHash(c+vec2(1,0));
            float d=basinHash(c+vec2(0,1)),e=basinHash(c+vec2(1,1));
            return f.x+f.y<1.0 ? a+(b-a)*f.x+(d-a)*f.y
              : e+(d-e)*(1.0-f.x)+(b-e)*(1.0-f.y);
          }
          float basinField(vec3 p) {
            return basinFacet(p.xz*.13)+.55*basinFacet(p.xy*.111)
              +.35*basinFacet(p.yz*.097)-.95;
          }\n` + shader.fragmentShader;
        shader.fragmentShader = shader.fragmentShader.replace('#include <color_fragment>', `#include <color_fragment>
          float field = basinField(vBasinLocal);
          float edge = 1.0-smoothstep(.009,.021,abs(field));
          ${ice ? 'diffuseColor.rgb *= .94 + .06*field - .045*edge;' : 'diffuseColor.rgb *= .8 + .2*field;'}
        `);
        if (!ice) shader.fragmentShader = shader.fragmentShader.replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
          // A thin cosmetic cooling skin does not provide solid support.
          float heat = .85 + .15*sin(uBasinTime*.104719755 + field*2.0);
          float exposed = smoothstep(.3,.6,basinFacet(vBasinLocal.xz*.037));
          totalEmissiveRadiance += vec3(1.5,.24,.018)*edge*heat*exposed;
        `);
      };
      m.customProgramCacheKey = () => `basin-v1-${kind}`;
      return m;
    };
    this.far = new THREE.Mesh(new THREE.SphereGeometry(def.seaRadius, 128, 96), material(new THREE.Vector3()));
    this.far.name = `basin:${kind}:far`;
    this.near = new THREE.Mesh(new THREE.PlaneGeometry(HALF * 2, HALF * 2, SEGMENTS, SEGMENTS), material(this.anchor));
    this.near.name = `basin:${kind}:near`;
    this.near.visible = false;
    this.near.receiveShadow = true;
    this.far.receiveShadow = true;
    // Keep the existing planet sea-layer visibility toggle authoritative.
    this.far.add(this.near);
  }

  /** Both meshes are children of the planet, so spin/rebases cannot slide them.
   * Positions really lie on the support sphere; no vertex-shader-only surface. */
  update(cameraLocal: THREE.Vector3, time?: number): void {
    if (time !== undefined) this.time.value = ((time % 600) + 600) % 600;
    const r = this.def.seaRadius!;
    const alt = cameraLocal.length() - r;
    this.near.visible = alt > -12 && alt < 600;
    if (!this.near.visible) return;
    this.up.copy(cameraLocal).normalize();
    this.point.copy(this.up).multiplyScalar(r);
    if (this.stats.rebuilds && this.point.distanceToSquared(this.anchor) < 96 ** 2) return;
    this.anchor.copy(this.point);
    this.near.position.copy(this.anchor);
    this.east.set(0, Math.abs(this.up.y) < .9 ? 1 : 0, Math.abs(this.up.y) < .9 ? 0 : 1).cross(this.up).normalize();
    this.north.crossVectors(this.up, this.east).normalize();
    const p = this.near.geometry.getAttribute('position') as THREE.BufferAttribute;
    const n = this.near.geometry.getAttribute('normal') as THREE.BufferAttribute;
    for (let j = 0; j <= SEGMENTS; j++) for (let i = 0; i <= SEGMENTS; i++) {
      this.point.copy(this.anchor).addScaledVector(this.east, (i / SEGMENTS * 2 - 1) * HALF)
        .addScaledVector(this.north, (1 - j / SEGMENTS * 2) * HALF).normalize();
      const k = j * (SEGMENTS + 1) + i;
      n.setXYZ(k, this.point.x, this.point.y, this.point.z);
      this.point.multiplyScalar(r).sub(this.anchor);
      p.setXYZ(k, this.point.x, this.point.y, this.point.z);
    }
    p.needsUpdate = n.needsUpdate = true;
    this.near.geometry.computeBoundingSphere();
    this.stats.rebuilds++;
  }

  setTime(time: number): void { this.time.value = ((time % 600) + 600) % 600; }
  dispose(): void {
    for (const mesh of [this.near, this.far]) {
      mesh.removeFromParent(); mesh.geometry.dispose(); (mesh.material as THREE.Material).dispose();
    }
  }
}
